// Inbox's own pages in Blueprint (pages/ux-full inbox/approval and inbox/notifications), drawn with
// ui-web's AgentRequest and NoteList (inbox-pages-model.ts builds their props):
// - To approve: one request on its own page (approvals.list), decided with approvals.decide exactly as
//   ApprovalsInbox decides it: Approve & run, "act as me for 12 hours" where it's offered, or Decline
//   with a note for the agent. The others still waiting are beside it, one tap each.
// - Notifications: the bell's list as a page (the same NotesState the bell reads); opening one marks it
//   read and goes where the bell's does; Mark read, Mark all read. What the agents did is beside it for
//   those who see the agents.
import { useEffect, useState } from 'react';
import {
  AgentActivity,
  AgentActivityProps,
  AgentRequest,
  NoteList,
  Section,
  type Decision,
} from '@flowology/ui-web';
import '@flowology/ui-web/base.css';
import { client } from '../api.ts';
import { Header, type NavProps } from '../Nav.tsx';
import { errorText, useQuery } from '../books/ui.tsx';
import { invoiceIdsIn } from '../books/model.ts';
import type { Approval } from '../books/types.ts';
import type { AgentsList } from '../agents/types.ts';
import { useOnline } from '../connection/connection.ts';
import { Loading } from '../shared/Loading.tsx';
import { shownText } from '../shared/pages.ts';
import { useSaveKey } from '../shared/SaveButton.tsx';
import type { Note } from '../shared/notifications-model.ts';
import { officeHost } from '../blueprint/host.ts';
import { noteListProps, requestProps, shownRequest } from './inbox-pages-model.ts';
import '../blueprint/inbox.css';

/** Whoever holds agents.view sees the agents themselves, so their stopped state and what they did. */
const VIEWERS = ['agents.view'];

export function ApprovalScreen({
  nav,
  timeZone,
  onAllRequests,
}: {
  nav: NavProps;
  timeZone: string;
  /** Agents and approvals › Approvals: every request, decided ones too. */
  onAllRequests: () => void;
}) {
  const held = nav.permissions ?? [];
  const pending = useQuery<{ approvals: Approval[] }>('approvals.list', { status: 'pending' });
  const agents = useQuery<AgentsList>('agents.list', VIEWERS.some((p) => held.includes(p)) ? {} : null);
  const stopped = new Map(agents.data?.agents.map((a) => [a.key, a.stoppedReason]) ?? []);
  const [wanted, setWanted] = useState<string | null>(null);
  // a request just decided stays on the page, as approvals.decide answered it, until another is picked
  const [answered, setAnswered] = useState<Approval | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const list = pending.data?.approvals ?? [];
  const shown = answered && answered.id === wanted ? answered : shownRequest(list, wanted);
  const others = list.filter((a) => a.id !== shown?.id);
  // the invoices its input names, by number (ApprovalsInbox reads them the same way, QA-267)
  const [numbers, setNumbers] = useState<ReadonlyMap<string, string>>(new Map());
  const ids = shown ? invoiceIdsIn(shown.input).filter((id) => !numbers.has(id)) : [];
  const wantedIds = ids.join(',');
  useEffect(() => {
    if (!wantedIds) return;
    let live = true;
    void Promise.all(
      wantedIds.split(',').map((id) =>
        client
          .call<{ number: string }>('invoices.get', { id })
          .then((r) => [id, r.number] as const)
          .catch(() => null),
      ),
    ).then((got) => {
      if (live) setNumbers((m) => new Map([...m, ...got.filter((g): g is readonly [string, string] => !!g)]));
    });
    return () => {
      live = false;
    };
  }, [wantedIds]);
  // one key per attempt at the same decision, so pressing again after a lost answer decides it once (QA-058)
  const approveKey = useSaveKey(`approve:${shown?.id ?? ''}`);
  const trustKey = useSaveKey(`trust:${shown?.id ?? ''}`);
  const declineKey = useSaveKey(`decline:${shown?.id ?? ''}`);
  const decide = async (a: Approval, d: Decision) => {
    setBusy(true);
    setError(null);
    const key = d.decision === 'decline' ? declineKey : d.trust ? trustKey : approveKey;
    try {
      const out = await client.call<Approval>(
        'approvals.decide',
        {
          approvalId: a.id,
          decision: d.decision,
          ...(d.note ? { note: d.note } : {}),
          ...(d.trust ? { trustConnection: true } : {}),
        },
        { idempotencyKey: key.key },
      );
      key.renew();
      // the decision's answer names no records: the words its row had stay
      setAnswered({ ...out, labels: out.labels ?? a.labels ?? {} });
      setWanted(out.id);
      await pending.reload();
    } catch (e) {
      setError(errorText(e, 'That didn’t go through. Try again.'));
    } finally {
      setBusy(false);
    }
  };
  const pick = (id: string) => {
    setWanted(id);
    setAnswered(null);
    setError(null);
  };
  const props = shown
    ? requestProps(shown, {
        timeZone,
        names: numbers,
        stopped: stopped.get(shown.agent.replace(/^agent:/, '')) ?? null,
      })
    : null;
  return (
    <div className="app">
      <Header
        nav={nav}
        title={props?.title ?? 'To approve'}
        // the request says who asked and when, under its own title
        {...(props
          ? {}
          : {
              lede: pending.data
                ? 'Nothing waits for your OK.'
                : 'What the agents and AI tools ask before they act.',
            })}
      />
      <main className="flw flw-office ib-pages">
        {!pending.data ? (
          <Loading error={pending.error} what="the requests" retry={() => void pending.reload()} />
        ) : (
          <div className="ib-cols">
            <div>
              {props && shown ? (
                <Section title="The request">
                  <AgentRequest
                    data={props}
                    host={officeHost(() => onAllRequests())}
                    busy={busy}
                    onDecide={(d) => void decide(shown, d)}
                  />
                  {error && (
                    <p className="error" role="alert">
                      {error}
                    </p>
                  )}
                </Section>
              ) : (
                <Section title="Waiting for you">
                  <p className="flw-empty" role="status">
                    Nothing waits for your OK. Agents ask before anything that contacts a customer, books work
                    or touches money, and their requests land here.
                  </p>
                </Section>
              )}
            </div>
            <div>
              <Section title="Also waiting" count={others.length}>
                {others.length ? (
                  <ul className="flw-rows">
                    {others.slice(0, 5).map((a) => {
                      const p = requestProps(a, { timeZone });
                      return (
                        <li key={a.id} className="flw-row">
                          <span>
                            <button type="button" className="flw-open" onClick={() => pick(a.id)}>
                              {p.title}
                            </button>
                            <span className="flw-sub">
                              {p.asker.name}, asked {p.asked}
                              {p.cannotDecide ? '. Not yours to decide' : ''}
                            </span>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="flw-sub">Nothing else waits for you.</p>
                )}
                <div className="flw-acts">
                  <button type="button" className="flw-link" onClick={onAllRequests}>
                    {others.length > 5
                      ? `All ${list.length} requests, and the decided ones`
                      : 'Every request, decided ones too'}
                  </button>
                </div>
              </Section>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export function NotificationsScreen({
  nav,
  timeZone,
  onApprovals,
}: {
  nav: NavProps;
  timeZone: string;
  /** What the agents did links to what waits for a person: To approve. */
  onApprovals: () => void;
}) {
  const notes = nav.notes;
  const online = useOnline();
  const held = nav.permissions ?? [];
  const activity = useQuery<unknown>('agents.activity', VIEWERS.some((p) => held.includes(p)) ? {} : null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // the last list shows at once; this brings it up to date, as the bell does on opening
  useEffect(() => {
    void notes?.reload();
  }, []);
  const list = notes?.notes ?? null;
  const unread = (list ?? []).filter((n) => !n.read).map((n) => n.id);
  const mark = async (key: string, ids: string[]) => {
    if (!notes || busy) return;
    setBusy(key);
    setError(null);
    try {
      await notes.markRead(ids);
    } catch (e) {
      setError(errorText(e, 'That didn’t go through. Try again.'));
    } finally {
      setBusy(null);
    }
  };
  const open = (n: Note) => {
    if (!n.read) void notes?.markRead([n.id]).catch(() => undefined);
    nav.onOpenNote?.(n);
  };
  const steps = activity.data ? AgentActivityProps.safeParse(activity.data) : null;
  return (
    <div className="app">
      <Header
        nav={nav}
        title="Notifications"
        lede={
          list
            ? notes?.unread
              ? `${notes.unread} unread. Each opens where it’s dealt with.`
              : 'Nothing new. Each opens where it’s dealt with.'
            : 'What happened that you may want to know.'
        }
      >
        {unread.length > 0 && (
          <button
            type="button"
            className="btn"
            disabled={!online || !!busy}
            onClick={() => void mark('all', unread)}
          >
            {busy === 'all' ? 'Marking…' : 'Mark all read'}
          </button>
        )}
      </Header>
      <main className="flw flw-office ib-pages">
        {!online && <p className="notice warn">No connection: new notifications arrive when it’s back.</p>}
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <div className="ib-cols">
          <div>
            <Section title="Your notifications" {...(list ? { count: notes?.total ?? list.length } : {})}>
              {!list ? (
                <Loading
                  error={notes?.error ?? null}
                  what="your notifications"
                  retry={() => void notes?.reload()}
                />
              ) : (
                <NoteList
                  data={noteListProps(list, new Date(), timeZone)}
                  busy={busy}
                  onOpen={(id) => {
                    const n = list.find((x) => x.id === id);
                    if (n) open(n);
                  }}
                  {...(online ? { onMarkRead: (id: string) => void mark(id, [id]) } : {})}
                />
              )}
              {list && (notes?.more || notes?.busy) && (
                <p className="flw-more">
                  <span className="muted small">{shownText(list.length, notes.total)}</span>{' '}
                  <button
                    type="button"
                    className="flw-btn"
                    disabled={notes.busy}
                    onClick={() => notes.more?.()}
                  >
                    {notes.busy ? 'Loading…' : 'Load more'}
                  </button>
                </p>
              )}
            </Section>
          </div>
          {steps?.success && (
            <div>
              <Section title="What the agents did today">
                <AgentActivity data={steps.data} host={officeHost(() => onApprovals())} />
              </Section>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
